import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { find, isEmpty } from 'lodash';
import LoadingBackdrop from '../../../atoms/loader/LoadingBackdrop';
import ChangeEmailButton from './ChangeEmailButton';
import ChangeEmailOTP from './ChangeEmailOTP';
import ChangeEmailPasswordForm from './ChangeEmailPasswordForm';
import ChangeNewEmail from './ChangeNewEmail';
import ChangeNewEmailSuccess from './ChangeNewEmailSuccess';
import {
  BottomContainer,
  ButtonWrapper,
  Content,
  Description,
  HeadlineStyle,
  MainContainer,
  Title,
} from './ChangeEmailStyle';
import { RootState, useAppDispatch } from '../../../store';
import { DashlineSeparator, contactSupportLinkStyle } from '../../../components/common/CommonStyle';
import TextLink from '../../../components/common/TextLink';
import { APPLE_AUTH, FULL_URL, GOOGLE_AUTH } from '../../../config/Master';
import GoogleButton from '../../../atoms/sso/GoogleButton';
import { handleSiteAttributes } from '../../../actions/attributeActions';
import { attachSocialLogin, setChangeEmailVerifyData } from '../../../actions/authActions';
import AppleButton from '../../../atoms/sso/AppleButton';

const ChangeEmail = (): ReactElement => {
  const [newEmail, setNewEmail] = useState('');
  const [newEmailCaptcha, setNewEmailCaptcha] = useState('');
  const [changeEmailStep, setChangeEmailStep] = useState('change_email_button');
  const { changeEmailVerifyData, isChangeEmailFetching } = useSelector((state: RootState) => state.auth);
  const { email, is_google_SSO_enabled, is_apple_SSO_enabled, social } = useSelector(
    (state: RootState) => state.settings
  );
  const dispatch = useAppDispatch();

  const mounted = useRef<boolean>(false);

  const socialAccounts = social?.data ?? [];

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    }

    //on page refresh if screen is email redirect to welcome screen and dispatch an action to reset the data
    if (!isEmpty(changeEmailVerifyData)) {
      setChangeEmailStep('change_email_button');
      dispatch(setChangeEmailVerifyData(null));
    }

    dispatch(handleSiteAttributes('member'));
  }, []);

  useEffect(() => {
    switch (changeEmailVerifyData?.redirectTo) {
      case 'change_new_email':
        setNewEmail('');
        setChangeEmailStep('change_new_email');
        break;
      case 'change_new_email_otp':
        setChangeEmailStep('change_new_email_otp');
        break;
      case 'change_email_success':
        setChangeEmailStep('change_email_success');
        break;
      case 'none':
        break;
      default:
        setChangeEmailStep('change_email_button');
        break;
    }
  }, [mounted, changeEmailVerifyData]);

  const renderChangeEmailStep = () => {
    switch (changeEmailStep) {
      case 'change_email_password':
        return (
          <ChangeEmailPasswordForm
            email={email}
            setChangeEmailStep={setChangeEmailStep}
            emailVerifyData={changeEmailVerifyData}
          />
        );
      case 'change_new_email':
        return (
          <ChangeNewEmail
            setChangeEmailStep={setChangeEmailStep}
            email={email}
            newEmail={newEmail}
            setNewEmail={setNewEmail}
            newEmailCaptcha={newEmailCaptcha}
            setNewEmailCaptcha={setNewEmailCaptcha}
            emailVerifyData={changeEmailVerifyData}
          />
        );
      case 'change_new_email_otp':
        return (
          <ChangeEmailOTP
            key="second_otp_step"
            showEdit={true}
            email={newEmail}
            setNewEmail={setNewEmail}
            setChangeEmailStep={setChangeEmailStep}
            showUsePassword={false}
            redirectStep={'change_new_email_otp'}
            setNewEmailCaptcha={setNewEmailCaptcha}
          />
        );
      case 'change_email_success':
        return <ChangeNewEmailSuccess email={newEmail} redirectTo={`/search`} />;
      case 'change_email_button':
      default:
        return <ChangeEmailButton email={email} socialAccounts={socialAccounts} />;
    }
  };

  const shouldDisplaySocialButtons = (type: string): boolean => {
    if (socialAccounts.length > 0) {
      return (
        !find(social.data, { social_site_name: type }) &&
        (type === GOOGLE_AUTH ? is_google_SSO_enabled : is_apple_SSO_enabled)
      );
    }

    return type === GOOGLE_AUTH ? is_google_SSO_enabled : is_apple_SSO_enabled;
  };

  const handleAttachGoogle = async (data: any) => {
    dispatch(attachSocialLogin({ ...data, meta: data }, GOOGLE_AUTH, 'connect', true));
  };

  const showGoogleButton = shouldDisplaySocialButtons(GOOGLE_AUTH);
  const showAppleButton = shouldDisplaySocialButtons('apple');

  return (
    <>
      <LoadingBackdrop active={isChangeEmailFetching} showLoadingBg={true} />

      <MainContainer>
        <Content>
          <Title>Change Email</Title>
          <DashlineSeparator />
          {renderChangeEmailStep()}
          {!['change_new_email_otp', 'change_new_email_otp', 'change_email_success'].includes(changeEmailStep) && (
            <>
              <DashlineSeparator />
              <BottomContainer>
                <HeadlineStyle>What if I lost access to my email?</HeadlineStyle>
                <Description style={{ letterSpacing: '-0.15px' }}>
                  {showGoogleButton || showAppleButton
                    ? ` We recommend setting up an alternative login method shown below or contact our support team and update
                  your email through them.`
                    : `We recommend contacting our support team and update your email through them.`}
                </Description>
                {(showGoogleButton || showAppleButton) && (
                  <ButtonWrapper>
                    {showGoogleButton && <GoogleButton onSubmit={handleAttachGoogle} />}
                    {showAppleButton && (
                      <AppleButton
                        onAppleConnect={(data: any) =>
                          dispatch(attachSocialLogin({ ...data, meta: data }, APPLE_AUTH, 'connect', true))
                        }
                      />
                    )}
                  </ButtonWrapper>
                )}
                <TextLink
                  to={`${FULL_URL}/faq/giving-feedback`}
                  style={{ ...contactSupportLinkStyle, textAlign: 'center', fontSize: 14 }}
                >
                  Contact Support
                </TextLink>
              </BottomContainer>
            </>
          )}
        </Content>
      </MainContainer>
    </>
  );
};

export default ChangeEmail;
